import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ChevronLeft, ChevronRight, ShoppingBag, Truck, PackageX, Clock, Check, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAITransition } from "@/components/AITransition";
import { addToCart } from "@/lib/cart";

const PINCODE_KEY = "celebrai_pincode";

const isAvailableForPin = (p, pincode) => {
  if (!p) return false;
  if (p.is_global) return true;
  const cp = String(pincode || "").trim();
  if (!cp) return false;
  const list = (p.available_pincodes || []).map((x) => String(x).trim());
  if (list.length) return list.includes(cp);
  const legacy = String(p.delivery_pincode || "").trim();
  return !!legacy && legacy === cp;
};

// Delivery fee helpers (per-product).
const productDeliveryFee = (p, defaultFee) => {
  const kind = (p?.delivery_fee_type || "free").toLowerCase();
  if (kind === "free") return 0;
  if (kind === "custom") return Math.max(0, parseInt(p?.delivery_fee_amount || 0, 10) || 0);
  return defaultFee;
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const playAI = useAITransition();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultDeliveryFee, setDefaultDeliveryFee] = useState(49);
  const [pincode, setPincode] = useState(() => localStorage.getItem(PINCODE_KEY) || "");
  const [imageIdx, setImageIdx] = useState(0);
  const [selectedWeight, setSelectedWeight] = useState(null);
  const [selectedFlavor, setSelectedFlavor] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // Preselect first variant/weight if present.
        if (r.data?.product_type === "cake") {
          const wv = r.data?.weight_variants || [];
          if (wv.length) setSelectedWeight(wv[0].weight_label);
          const fl = r.data?.flavors || [];
          if (fl.length) setSelectedFlavor(fl[0]);
        } else {
          const v = r.data?.variants || [];
          if (v.length) setSelectedVariant(v[0].option_label);
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    api.get("/settings/public")
      .then((r) => setDefaultDeliveryFee(r.data.default_delivery_fee ?? 49))
      .catch(() => {});
  }, [id]);

  const images = useMemo(() => {
    if (!product) return [];
    return product.images && product.images.length ? product.images : (product.image_url ? [product.image_url] : []);
  }, [product]);

  const effectivePrice = useMemo(() => {
    if (!product) return 0;
    if (product.product_type === "cake" && selectedWeight) {
      const wv = (product.weight_variants || []).find((v) => v.weight_label === selectedWeight);
      if (wv) return wv.price || product.price;
    }
    if (product.product_type !== "cake" && selectedVariant) {
      const v = (product.variants || []).find((x) => x.option_label === selectedVariant);
      if (v) return v.price || product.price;
    }
    return product.price;
  }, [product, selectedWeight, selectedVariant]);

  const deliverable = productDeliveryFee(product, defaultDeliveryFee) === 0;
  const available = isAvailableForPin(product, pincode);
  const cakeMissingWeights =
    product?.product_type === "cake" && (product?.weight_variants || []).length === 0;
  const canBook =
    product?.stock_status === "available" &&
    product?.available !== false &&
    !cakeMissingWeights &&
    available &&
    (product?.product_type !== "cake" || selectedWeight);

  const savePincode = () => {
    if (!/^\d{6}$/.test(pincode)) { toast.error("Enter a valid 6-digit pincode"); return; }
    localStorage.setItem(PINCODE_KEY, pincode);
    if (!isAvailableForPin(product, pincode)) {
      toast.error("This item is not available for your location");
    } else if (deliverable) {
      toast.success("Free delivery to your area");
    } else {
      toast.success(`Delivery fee ₹${productDeliveryFee(product, defaultDeliveryFee)} applies`);
    }
  };

  const addToCartHandler = () => {
    if (!canBook) return;
    // Encode selection into a stable line_key so different variants live in
    // separate cart rows without touching the existing cart contract.
    const variantParts = [];
    if (selectedWeight) variantParts.push(`w:${selectedWeight}`);
    if (selectedFlavor) variantParts.push(`f:${selectedFlavor}`);
    if (selectedVariant) variantParts.push(`v:${selectedVariant}`);
    const variantKey = variantParts.join("|");
    const lineKey = variantKey ? `${product.id}::${variantKey}` : product.id;

    const nameWithVariant = variantParts.length
      ? `${product.name} — ${[selectedWeight, selectedFlavor, selectedVariant].filter(Boolean).join(", ")}`
      : product.name;

    // Prefer the image the user is actually viewing in the gallery so
    // variant/angle-specific hero shots persist into the cart.
    const cartImage =
      images[imageIdx] || product.image_url || (product.images && product.images[0]) || "";

    addToCart({
      line_key: lineKey,
      kind: "product",
      product_id: product.id,
      name: nameWithVariant,
      category: product.category,
      price: effectivePrice,
      quantity: 1,
      image_url: cartImage,
      is_global: !!product.is_global,
      delivery_pincode: product.delivery_pincode != null ? String(product.delivery_pincode).trim() : null,
      selected_weight: selectedWeight || null,
      selected_flavor: selectedFlavor || null,
      selected_variant: selectedVariant || null,
    });
    toast.success(`${product.name} added to cart`);
  };

  if (loading) {
    return <div className="py-24 text-center text-charcoal/60" data-testid="pd-loading">Loading product…</div>;
  }
  if (!product) {
    return (
      <div className="py-24 text-center" data-testid="pd-not-found">
        <p className="font-display text-3xl text-charcoal mb-2">Product not found</p>
        <button onClick={() => navigate("/shop")} className="pill-btn pill-btn-primary mt-4">
          Back to shop
        </button>
      </div>
    );
  }

  const stockPill = {
    available: { label: "In stock", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    coming_soon: { label: "Coming Soon", cls: "bg-amber-100 text-amber-700 border-amber-200" },
    out_of_stock: { label: "Out of Stock", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  }[product.stock_status || "available"] || { label: "In stock", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-charcoal/70 hover:text-charcoal font-semibold"
        data-testid="pd-back"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div className="space-y-3">
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-linen-2 shadow-sm" data-testid="pd-gallery">
            <AnimatePresence mode="wait">
              <motion.img
                key={imageIdx}
                src={images[imageIdx]}
                alt={product.name}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="w-full h-full object-cover"
                data-testid={`pd-image-${imageIdx}`}
              />
            </AnimatePresence>
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setImageIdx((i) => (i - 1 + images.length) % images.length)}
                  className="absolute top-1/2 left-3 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow-md"
                  data-testid="pd-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setImageIdx((i) => (i + 1) % images.length)}
                  className="absolute top-1/2 right-3 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white shadow-md"
                  data-testid="pd-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setImageIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === imageIdx ? "bg-white w-6" : "bg-white/60 w-1.5"
                      }`}
                      data-testid={`pd-dot-${i}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {images.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setImageIdx(i)}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                    i === imageIdx ? "border-rose-400 ring-2 ring-rose-200" : "border-warm hover:border-charcoal/30"
                  }`}
                  data-testid={`pd-thumb-${i}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500 capitalize" data-testid="pd-category">
              {product.category}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl text-charcoal mt-1 leading-tight" data-testid="pd-name">
              {product.name}
            </h1>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${stockPill.cls}`}
                data-testid="pd-stock"
              >
                {stockPill.label}
              </span>
              {product.min_lead_time_days > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                  data-testid="pd-lead-time">
                  <Clock className="w-3 h-3" />
                  {product.min_lead_time_days} day lead time
                </span>
              )}
            </div>
          </div>

          <p className="font-display text-4xl text-charcoal" data-testid="pd-price">
            ₹{effectivePrice.toLocaleString("en-IN")}
          </p>

          {/* Cake selectors */}
          {product.product_type === "cake" && (product.weight_variants || []).length > 0 && (
            <div data-testid="pd-weight-selector">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2">Weight</p>
              <div className="flex flex-wrap gap-2">
                {product.weight_variants.map((v) => (
                  <button
                    key={v.weight_label}
                    onClick={() => setSelectedWeight(v.weight_label)}
                    data-testid={`pd-weight-${v.weight_label}`}
                    className={`rounded-2xl px-4 py-2.5 text-sm font-semibold border-2 transition-all ${
                      selectedWeight === v.weight_label
                        ? "border-rose-500 bg-rose-50 text-charcoal shadow-sm"
                        : "border-warm bg-white text-charcoal/70 hover:border-charcoal/30"
                    }`}
                  >
                    <span className="block">{v.weight_label}</span>
                    <span className="block text-[11px] text-charcoal/60 mt-0.5">₹{v.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.product_type === "cake" && (product.flavors || []).length > 0 && (
            <div data-testid="pd-flavor-selector">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2">Flavor</p>
              <div className="flex flex-wrap gap-2">
                {product.flavors.map((f) => (
                  <button
                    key={f}
                    onClick={() => setSelectedFlavor(f)}
                    data-testid={`pd-flavor-${f}`}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-all ${
                      selectedFlavor === f
                        ? "border-rose-500 bg-rose-500 text-white"
                        : "border-warm bg-white text-charcoal/70 hover:border-charcoal/30"
                    }`}
                  >
                    {f}
                    {selectedFlavor === f && <Check className="inline w-3 h-3 ml-1" strokeWidth={3} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Non-cake variant selector */}
          {product.product_type !== "cake" && (product.variants || []).length > 0 && (
            <div data-testid="pd-variant-selector">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2">Options</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.option_label}
                    onClick={() => setSelectedVariant(v.option_label)}
                    data-testid={`pd-variant-${v.option_label}`}
                    className={`rounded-2xl px-4 py-2.5 text-sm font-semibold border-2 transition-all ${
                      selectedVariant === v.option_label
                        ? "border-rose-500 bg-rose-50 text-charcoal shadow-sm"
                        : "border-warm bg-white text-charcoal/70 hover:border-charcoal/30"
                    }`}
                  >
                    <span className="block">{v.option_label}</span>
                    <span className="block text-[11px] text-charcoal/60 mt-0.5">₹{v.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Delivery pincode / tag */}
          <div className="card-surface p-4" data-testid="pd-delivery-card">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2">Delivery</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter 6-digit pincode"
                className="flex-1 rounded-xl bg-white border border-warm px-4 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
                data-testid="pd-pincode-input"
              />
              <button
                type="button"
                onClick={savePincode}
                className="rounded-full px-4 py-2.5 text-xs font-bold bg-charcoal text-white"
                data-testid="pd-pincode-check"
              >
                Check
              </button>
            </div>
            {pincode && /^\d{6}$/.test(pincode) && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {!available ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                    data-testid="pd-not-available">
                    <PackageX className="w-3 h-3" /> Not available for your location
                  </span>
                ) : deliverable ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                    data-testid="pd-delivery-free">
                    <Truck className="w-3 h-3" /> Free delivery
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                    data-testid="pd-delivery-paid">
                    <PackageX className="w-3 h-3" /> Delivery ₹{productDeliveryFee(product, defaultDeliveryFee)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Selection preview */}
          {(selectedWeight || selectedFlavor || selectedVariant) && (
            <div className="text-xs text-charcoal/60" data-testid="pd-selection-summary">
              You&apos;re adding:{" "}
              <span className="font-semibold text-charcoal">
                {[selectedWeight, selectedFlavor, selectedVariant].filter(Boolean).join(" · ")}
              </span>
            </div>
          )}

          {/* Add to cart */}
          <button
            onClick={addToCartHandler}
            disabled={!canBook}
            className="w-full rounded-full px-6 py-4 text-sm font-bold text-white shadow-lg shadow-rose-500/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
            data-testid="pd-add-to-cart"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={2.25} />
            {product.stock_status === "coming_soon" ? "Coming soon" :
              product.stock_status === "out_of_stock" ? "Out of stock" :
              !available ? "Not available for your location" :
              "Add to cart"}
          </button>

          {/* Contextual AI CTA — opens the EXISTING planner, with this product
              passed as context so Gemini knows what the customer is viewing. */}
          <button
            onClick={(e) => playAI(() => navigate(`/planner?product=${encodeURIComponent(id)}`), e)}
            className="w-full rounded-full px-6 py-3 text-sm font-bold border-2 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white transition-colors flex items-center justify-center gap-2"
            data-testid="pd-plan-celebration"
          >
            <Sparkles className="w-4 h-4" strokeWidth={2.25} /> Plan a celebration around this
          </button>

          {/* Description */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2">About this product</p>
            <p className="text-sm text-charcoal/80 leading-relaxed whitespace-pre-line" data-testid="pd-description">
              {product.description}
            </p>
            {(product.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {product.tags.map((t, i) => (
                  <span key={i} className="text-[10px] uppercase tracking-widest font-semibold text-charcoal/50 bg-linen-2 rounded-full px-2 py-0.5">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
